import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useDeal, useDeleteDeal, usePipelines, useSaveDeal } from "@/api/crm";
import { useFields, useMe } from "@/api/hooks";
import type { Stage } from "@/api/types";
import { useQuotes } from "@/api/sales";
import { StatusBadge } from "@/components/crm/Badge";
import { QuoteStatusBadge } from "@/features/quotes/QuoteStatusBadge";
import { formatCustom, useFieldLabels } from "@/components/crm/CustomFieldsFieldset";
import { ActivityComposer, Timeline } from "@/components/crm/Timeline";
import { Icon } from "@/components/icons";
import { Alert, Modal, PageHeader, Spinner } from "@/components/ui";
import { money, shortDate } from "@/lib/format";
import { can } from "@/lib/permissions";
import { CallButtons } from "@/features/calls/CallButtons";
import { SamplesPanel } from "@/features/samples/SamplesPanel";
import { AiSummary, DraftButton, ScoreCard } from "@/features/ai/AiPanels";
import type { Draft } from "@/api/service";
import { DealForm } from "./DealForm";
import { LostReasonDialog } from "./LostReasonDialog";

const FORECAST: Record<string, string> = { pipeline: "Pipeline", best_case: "Best case", commit: "Commit", omitted: "Leave out" };

/** An AI draft to copy into your email; nothing is sent from here. */
function DraftDialog({ draft, onClose }: { draft: Draft; onClose: () => void }) {
  const [subject, setSubject] = useState(draft.subject ?? "");
  const [body, setBody] = useState(draft.body);
  const [copied, setCopied] = useState(false);
  return (
    <Modal title="AI draft" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-muted">Edit it, then copy it into your email. Text in [check: …] needs a fact from you.</p>
        <div><label htmlFor="dr-subject" className="label">Subject</label><input id="dr-subject" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
        <div><label htmlFor="dr-body" className="label">Message</label><textarea id="dr-body" className="input min-h-56" value={body} onChange={(e) => setBody(e.target.value)} /></div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
          <button type="button" className="btn-primary" onClick={() => { void navigator.clipboard?.writeText(`${subject}\n\n${body}`); setCopied(true); }}>{copied ? "Copied" : "Copy"}</button>
        </div>
      </div>
    </Modal>
  );
}

export function DealDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const perms = useMe().data?.permissions ?? [];
  const deal = useDeal(id);
  const pipelines = usePipelines();
  const fields = useFields("deal");
  const labels = useFieldLabels("deal");
  const save = useSaveDeal(id);
  const [draft, setDraft] = useState<Draft | null>(null);
  const quotes = useQuotes({ deal_id: id }, can(perms, "quotes:read"));
  const del = useDeleteDeal();
  const [editing, setEditing] = useState(false);
  const [askLost, setAskLost] = useState<Stage | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (deal.isLoading) return <Spinner />;
  if (deal.error) {
    const notFound = deal.error instanceof ApiError && deal.error.status === 404;
    return <Alert>{notFound ? "This deal does not exist or you do not have access to it." : deal.error.message}</Alert>;
  }
  const d = deal.data!;
  const stages = pipelines.data?.find((p) => p.id === d.pipeline.id)?.stages ?? [];
  const openStages = stages.filter((s) => s.kind === "open");
  const won = stages.find((s) => s.kind === "won");
  const lost = stages.find((s) => s.kind === "lost");
  const currentIndex = openStages.findIndex((s) => s.id === d.stage.id);
  const canEdit = can(perms, "deals:update");

  const moveTo = async (stage: Stage, lostReason?: string) => {
    setError(null);
    try { await save.mutateAsync({ stage_id: stage.id, ...(lostReason ? { lost_reason: lostReason } : {}) }); }
    catch (e) { setError(e instanceof ApiError ? Object.values(e.fieldErrors())[0] ?? e.message : "Could not update the stage"); }
  };

  const details: [string, string | null][] = [
    ["Value", money(d.amount, d.currency)], ["Expected close", shortDate(d.expected_close_date) || null],
    ["Pipeline", d.pipeline.name], ["Owner", d.owner?.full_name ?? null],
    ["Days in stage", String(d.days_in_stage)], ["Created", shortDate(d.created_at)],
    ...(!canEdit ? [["Forecast", FORECAST[d.forecast_category ?? "pipeline"]] as [string, string]] : []),
    ...(d.status === "lost" ? [["Lost reason", d.lost_reason] as [string, string | null]] : []),
    ...(fields.data ?? []).map((f): [string, string | null] => [f.label, formatCustom(d.custom[f.key])]),
  ];

  return (
    <>
      <Link to="/deals" className="mb-4 inline-block text-sm text-muted hover:text-brand">← Pipeline</Link>
      <PageHeader title={d.name}
        subtitle={[d.account?.name, d.contact?.full_name].filter(Boolean).join(" · ") || undefined}
        actions={<>
          {d.contact && can(perms, "activities:create") && <CallButtons contactId={d.contact.id} dealId={d.id} name={d.contact.full_name} />}
          {canEdit && d.status === "open" && won && <button type="button" className="btn border border-ok/40 bg-surface text-ok hover:bg-ok/5" onClick={() => void moveTo(won)}><Icon name="trophy" />Won</button>}
          {canEdit && d.status === "open" && lost && <button type="button" className="btn-danger" onClick={() => setAskLost(lost)}>Lost</button>}
          {canEdit && d.status !== "open" && openStages.at(-1) && <button type="button" className="btn-ghost" onClick={() => void moveTo(openStages.at(-1)!)}>Reopen</button>}
          {canEdit && <button type="button" className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>}
          {can(perms, "deals:delete") && <button type="button" className="btn-ghost text-danger" aria-label="Delete deal" onClick={() => {
            if (window.confirm(`Delete ${d.name}?`)) void del.mutateAsync(d.id).then(() => navigate("/deals"));
          }}>Delete</button>}
        </>} />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <StatusBadge status={d.status} />
        <span className="text-2xl font-semibold">{money(d.amount, d.currency)}</span>
        {d.status === "lost" && d.lost_reason && <span className="text-sm text-danger">Lost: {d.lost_reason}</span>}
      </div>

      <nav aria-label="Stages" className="mb-6 flex overflow-x-auto rounded-lg border border-line bg-surface">
        {openStages.map((s, i) => {
          const done = d.status === "won" || (d.status === "open" && i < currentIndex);
          const current = d.status === "open" && i === currentIndex;
          return (
            <button key={s.id} type="button" disabled={!canEdit || current} onClick={() => void moveTo(s)} aria-current={current ? "step" : undefined}
              className={`relative min-w-32 flex-1 px-3 py-2.5 text-xs font-medium transition ${current ? "bg-brand text-white" : done ? "bg-brand-soft text-brand" : "text-muted hover:bg-canvas"} ${i > 0 ? "border-l border-line" : ""}`}>
              {s.name}
            </button>
          );
        })}
      </nav>
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
        <section className="card min-w-0 p-6" aria-label="Details">
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <div><dt className="text-xs font-medium uppercase tracking-wide text-muted">Account</dt><dd className="mt-0.5 text-sm">{d.account ? <Link className="text-brand hover:underline" to={`/accounts/${d.account.id}`}>{d.account.name}</Link> : "—"}</dd></div>
            <div><dt className="text-xs font-medium uppercase tracking-wide text-muted">Contact</dt><dd className="mt-0.5 text-sm">{d.contact ? <Link className="text-brand hover:underline" to={`/contacts/${d.contact.id}`}>{d.contact.full_name}</Link> : "—"}</dd></div>
            {d.dealer && <div><dt className="text-xs font-medium uppercase tracking-wide text-muted">Through dealer</dt><dd className="mt-0.5 text-sm"><Link className="text-brand hover:underline" to={`/accounts/${d.dealer.id}`}>{d.dealer.name}</Link></dd></div>}
            {canEdit && <div><dt className="text-xs font-medium uppercase tracking-wide text-muted"><label htmlFor="forecast-category">Forecast</label></dt>
              <dd className="mt-0.5"><select id="forecast-category" className="input w-auto py-1 text-sm" value={d.forecast_category ?? "pipeline"}
                onChange={(e) => void save.mutateAsync({ forecast_category: e.target.value })}>
                {Object.entries(FORECAST).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></dd></div>}
            {details.map(([label, value]) => (
              <div key={label}><dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt><dd className="mt-0.5 text-sm">{value ?? <span className="text-muted">—</span>}</dd></div>
            ))}
          </dl>
        </section>
        {can(perms, "quotes:read") && (
          <section className="card" aria-label="Quotes">
            <div className="flex items-center justify-between border-b border-line px-5 py-3">
              <h2 className="font-semibold">Quotes</h2>
              {can(perms, "quotes:create") && <Link to={`/quotes/new?deal=${d.id}`} className="btn-ghost text-xs">New quote</Link>}
            </div>
            {quotes.data?.items.length ? (
              <ul className="divide-y divide-line">
                {quotes.data.items.map((qt) => (
                  <li key={qt.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <Link to={`/quotes/${qt.id}`} className="font-medium hover:text-brand">{qt.number}{qt.version > 1 && ` rev. ${qt.version}`}<span className="block text-xs font-normal text-muted">Valid until {shortDate(qt.valid_until)}</span></Link>
                    <span className="flex items-center gap-3 text-sm">{money(qt.grand_total, qt.currency)}<QuoteStatusBadge status={qt.display_status} /></span>
                  </li>
                ))}
              </ul>
            ) : <p className="px-5 py-4 text-sm text-muted">No quotes yet.</p>}
          </section>
        )}
        <SamplesPanel dealId={d.id} canEdit={canEdit} />
        </div>
        <div className="min-w-0 space-y-6">
          <ScoreCard key={d.id} dealId={d.id} score={d.score} reasons={d.score_reasons} />
          <AiSummary objectType="deal" id={d.id} />
          {d.status === "open" && d.contact && <DraftButton label="Draft a follow-up email" body={{ kind: "follow_up", deal_id: d.id }} onDraft={setDraft} />}
        <section className="card min-w-0 space-y-5 p-5" aria-label="Activity">
          <h2 className="font-semibold">Activity</h2>
          {can(perms, "activities:create") && <ActivityComposer target={{ type: "deal", id: d.id }} />}
          <Timeline target={{ type: "deal", id: d.id }} labels={labels} />
        </section>
        </div>
      </div>
      {draft && <DraftDialog draft={draft} onClose={() => setDraft(null)} />}
      {editing && <Modal title={`Edit ${d.name}`} onClose={() => setEditing(false)}><DealForm deal={d} onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} /></Modal>}
      {askLost && <LostReasonDialog dealName={d.name} onCancel={() => setAskLost(null)} onConfirm={(r) => { const s = askLost; setAskLost(null); void moveTo(s, r); }} />}
    </>
  );
}
